import React, { ComponentProps, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Pane, DateRangePicker, Flex, DateRangePickerHandle } from '@fullstory/components';
import { TimeInterval } from '@fullstory/proto/fs/api/segment';
import { DateRangeID } from '@fullstory/client-api';
import { MultiColumnTimePickerList } from '../MultiColumnTimePickerList';
import {
  comparisons,
  ComparisonID,
  isCannedID,
  IntervalOption,
  getFormattedDate,
  shouldIncludeTimeInputs,
} from './utils';
import { DateRangeComparison, getComparisonDateRange } from '../../utils/comparison';
import { DateRange, DateRangeOption } from '../../types/dateRange';
import { isNonNull } from '@fullstory/typeutils';
import { isEqual } from 'lodash';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useDatePickerRetentionProps } from '../../hooks/useDatePickerRetentionProps';
import { FocusPosition, RangeIndex } from '@fullstory/components/legacy/DateRangePicker/types';
import { useFlags } from '@fullstory/react-hooks';

export const testIds = {
  MULTI_COLUMN_TIME_PICKER: 'multi-column-time-picker',
  PRIMARY_RANGE_LIST: 'primary-range-list',
  COMPARISON_RANGE_LIST: 'comparison-range-list',
  TIME_INTERVAL_LIST: 'time-interval-list',
};

interface MultiColumnTimePickerProps {
  primaryRange: DateRange | null;
  onPrimaryRangeChange: (value: DateRange) => void;
  dateRangeId: DateRangeID | null;
  onDateRangeIdChange: (value: DateRangeID) => void;
  comparison: DateRangeComparison | null;
  onComparisonChange: (value: DateRangeComparison) => void;
  interval: TimeInterval | null;
  onIntervalChange: (interval: TimeInterval) => void;
  primaryRangeOptions: DateRangeOption[];
  intervalOptions: IntervalOption[];
  focusComparison: boolean;
  /**
   * If a retentionTimestamp is provided, it will be used to disable any calendar days before the timestamp.
   * If this behavior is not desired, leave the retentionTimestamp undefined.
   */
  retentionTimestamp?: number;
  onMetricDetailForm?: boolean;
}

export const MultiColumnTimePicker: React.FC<MultiColumnTimePickerProps> = ({
  primaryRange,
  onPrimaryRangeChange,
  dateRangeId,
  onDateRangeIdChange,
  comparison,
  onComparisonChange,
  interval,
  onIntervalChange,
  primaryRangeOptions,
  intervalOptions,
  focusComparison,
  retentionTimestamp,
  onMetricDetailForm,
}) => {
  const intl = useIntl();
  const flags = useFlags();
  const picker = useRef<DateRangePickerHandle>(null);
  const comparisonRange = useMemo(() => {
    if (primaryRange && comparison) {
      return getComparisonDateRange(primaryRange, comparison);
    }
    return null;
  }, [primaryRange, comparison]);

  const comparisonOnChange = (id: ComparisonID) => {
    if (isCannedID(id)) {
      onComparisonChange({ canned: id });
    } else {
      onComparisonChange({ custom: { start: null, end: null } });
    }
    // focus comparison
    picker.current?.focus([RangeIndex.Comparison, FocusPosition.Start]);
  };

  useEffect(() => {
    if (focusComparison) picker.current?.focus([1, 0]);
  }, [focusComparison]);

  const dateRangeIsActiveItem = (id: DateRangeID): boolean => dateRangeId === id;

  const intervalIsActiveItem = (id: TimeInterval) => interval === id;

  const comparisonIsActiveItem = useCallback(
    (id: ComparisonID): boolean => {
      if (isCannedID(id)) {
        return comparison?.canned === id;
      }
      return !!comparison?.custom;
    },
    [comparison?.canned, comparison?.custom],
  );

  const comparisonsWithSubtext = useMemo(
    () =>
      comparisons.map(comparison => {
        if (comparisonIsActiveItem(comparison.id)) {
          const comparisonSubText =
            comparisonRange && comparisonRange.end
              ? intl.formatMessage(
                  { defaultMessage: 'Ending {endDateFormatted}', id: 'jQSP3O' },
                  { endDateFormatted: getFormattedDate(comparisonRange.end) },
                )
              : undefined;
          return {
            ...comparison,
            subText: comparisonSubText,
          };
        }
        return comparison;
      }),
    [comparisonIsActiveItem, comparisonRange, intl],
  );

  const ranges = useMemo(() => {
    let adjustedPrimaryRange = primaryRange ?? {
      start: null,
      end: null,
    };
    // All time is saved as an empty start and end date but display the range as all days in the retention period
    if (dateRangeId === DateRangeID.AllTime && retentionTimestamp) {
      adjustedPrimaryRange = {
        start: new Date(retentionTimestamp),
        end: moment().add(1, 'day').startOf('day').toDate(),
      };
    }

    const list: ComponentProps<typeof DateRangePicker>['ranges'] = [adjustedPrimaryRange];
    if (comparisonRange) list.push(comparisonRange);
    return list;
  }, [comparisonRange, primaryRange, dateRangeId, retentionTimestamp]);

  const includeTimeInputs = shouldIncludeTimeInputs(
    dateRangeId,
    interval,
    !flags.featureFlags().AppDatePickerTimeInputs,
    ranges[RangeIndex.Primary].end,
  );

  const { isDayDisabled, renderInfoTooltip, minDate } =
    useDatePickerRetentionProps(retentionTimestamp);

  return (
    <>
      <Pane width="min-content" overflow="hidden" testId={testIds.MULTI_COLUMN_TIME_PICKER}>
        <Flex alignItems="stretch">
          {interval && (
            <MultiColumnTimePickerList<TimeInterval>
              items={intervalOptions}
              onChange={onIntervalChange}
              isActiveItem={intervalIsActiveItem}
              iconColor="cta"
              testId={testIds.TIME_INTERVAL_LIST}
            />
          )}
          <MultiColumnTimePickerList<DateRangeID>
            items={primaryRangeOptions}
            onChange={(id: DateRangeID) => {
              onDateRangeIdChange(id);
              // focus primary
              picker.current?.focus([0, 0]);
            }}
            isActiveItem={dateRangeIsActiveItem}
            iconColor="cta"
            testId={testIds.PRIMARY_RANGE_LIST}
            showCustomTimeRangeWarning={onMetricDetailForm}
          />
          {comparison && (
            <MultiColumnTimePickerList<ComparisonID>
              items={comparisonsWithSubtext}
              onChange={comparisonOnChange}
              isActiveItem={comparisonIsActiveItem}
              iconColor="brand"
              title={intl.formatMessage({ defaultMessage: 'Compared to...', id: 'DwYfJu' })}
              testId={testIds.COMPARISON_RANGE_LIST}
            />
          )}
          <Box height={480}>
            <DateRangePicker
              ref={picker}
              ranges={ranges}
              onChange={ranges => {
                const [primary, comparison] = ranges;
                // only update ranges if they have changed
                if (!isRangeEqual(primary, primaryRange)) {
                  onPrimaryRangeChange(primary);
                }
                if (comparison && !isRangeEqual(comparison, comparisonRange)) {
                  onComparisonChange({ custom: comparison });
                }
              }}
              isDayDisabled={isDayDisabled}
              renderInfoTooltip={renderInfoTooltip}
              minDate={minDate}
              ongoing={dateRangeId === DateRangeID.Since}
              includeTimeInputs={includeTimeInputs}
              timeDisplayMode={includeTimeInputs ? 'never' : 'auto'}
              preserveRangeEnd={includeTimeInputs}
              v2={flags.featureFlags().AppDatePickerTimeInputs}
            />
          </Box>
        </Flex>
      </Pane>
    </>
  );
};

// function that checks if two ranges are equal
const isRangeEqual = (a: DateRange | null, b: DateRange | null) => {
  // not equal if either range is null
  if (!isNonNull(a) || !isNonNull(b)) return false;
  // not equal if one of them is null and the other isn't
  if (isNonNull(a.start) !== isNonNull(b.start)) return false;
  if (isNonNull(a.end) !== isNonNull(b.end)) return false;
  // not equal if both are non-null but the start or end dates are different
  if (isNonNull(a.start) && isNonNull(b.start) && !isEqual(a.start, b.start)) return false;
  if (isNonNull(a.end) && isNonNull(b.end) && !isEqual(a.end, b.end)) return false;
  return true;
};
